import { Middleware, createAction } from "@reduxjs/toolkit";
import { Socket, io } from "socket.io-client";

import { SendMessagePayload } from "@/types/message";
import { syncMessage, setReloadMessages } from "@/features/chat/chatSlice";
import { invalidateCacheEntry } from "@/features/chat/conversationCacheSlice";
import { AppDispatch } from "./store";
import { loadConversations, keyExchangeReceived } from "@/features/control/controlSlice";
import { logout } from "@/features/auth/authSlice";
import { fetchMessages } from "@/services/conversationService";
import { GROUP_ADDRESS_TYPE, USERID_ADDRESS_TYPE } from "@/constants/message";
import { processIncomingMessage } from "@/utils/encryptionProcessor";
import {
  initializeEncryption,
  requestPublicKey,
  refreshPublicKey,
} from "@/services/keyExchangeService";
import { getPublicKeySetByUsername } from "@/services/keyStorageService";
import { encryptMessage } from "@/utils/crypto";
import {
  isGroupKeyRequestMessage,
  parseGroupKeyRequestMessage,
  answerGroupKeyRequest,
  requestGroupKey,
  catchUpGroupKeys,
  normalizeGroupAddress,
} from "@/services/groupKeyService";
import { getUsers } from "@/services/userGroupService";

let socket: Socket | null = null;

export const connectSocket = createAction<{userId: number; sessionId: string}>("socket/connect");
export const disconnectSocket = createAction("socket/disconnect");
export const sendMessageSocket =
  createAction<SendMessagePayload>("socket/sendMessage");

const websocketMiddleware: Middleware = (store) => (next) => (action) => {
  if (connectSocket.match(action)) {
    const { userId, sessionId } = action.payload;

    // If a socket is already active (connected or still establishing), don't interrupt it.
    // This prevents the "WebSocket closed before connection established" warning when
    // connectSocket is dispatched multiple times (e.g., from both login and checkAuthStatus).
    if (socket?.active) {
      return next(action);
    }

    // Clean up any inactive/dead socket before creating a new one
    if (socket) {
      socket.disconnect();
    }

    socket = io({
      reconnectionAttempts: 10,
      withCredentials: true, // Required for the browser to send session cookies during handshake
      auth: { 
        sessionId,
        userId
      },
    }); // note: url defaults to window.location.origin

    const dispatch: AppDispatch = store.dispatch;

    // A NEW key arrived for `keyAddress` (canonical group address or peer username). Rows that
    // failed under the old key are permanent and won't retry on their own, so re-decrypt that
    // conversation: reload it if open, else drop its cache entry so the next open re-fetches.
    const redecryptConversation = (keyAddress: string) => {
      const state = store.getState();
      const { conversations: convs, currConversationIndex: openIdx } = state.control;
      const openConv = openIdx !== null ? convs[openIdx] : null;
      if (openConv && normalizeGroupAddress(openConv.address) === keyAddress) {
        dispatch(setReloadMessages(true));
      } else {
        const staleEntryKey = Object.keys(state.conversationCache.entries).find(
          (k) => normalizeGroupAddress(k) === keyAddress
        );
        if (staleEntryKey) dispatch(invalidateCacheEntry(staleEntryKey));
      }
    };

    /**
     * Minimal helper to decrypt messages before dispatching.
     * Preserves the exact original object structure to avoid breaking chatSlice logic.
     */
    const processAndDispatch = async (raw: any) => {
      try {
        const authUserId = store.getState().auth.userId;

        // Extract the message text from either nested or flat structures
        const messageText = raw.formattedMsg?.message || raw.message;

        if (authUserId && messageText) {
          // The WebSocket server JSON.parse()s group addresses into objects.
          // Stringify them back so IDB lookups (keyed by the canonical address string) work.
          const rawAddress = raw.formattedMsg?.address ?? raw.address;
          const peerAddress = typeof rawAddress === 'object' && rawAddress !== null
            ? JSON.stringify(rawAddress)
            : rawAddress;

          // Pre-resolve sender username for group messages that use the 3-part Android wire
          // format (IV:ciphertext:signature) — no username is embedded in the cipher itself.
          // rawAddress.mid is the sender's user ID, set by the WebSocket server from the
          // group routing address JSON.
          let resolvedSenderUsername: string | undefined;
          if (raw.isGroup && typeof rawAddress === 'object' && rawAddress !== null && rawAddress.mid) {
            const senderId = Number(rawAddress.mid);
            if (senderId) {
              try {
                const users = await getUsers();
                resolvedSenderUsername = users.find((u) => u.id === senderId)?.username;
              } catch (err) {
                console.debug('[GroupMsg] Could not resolve sender username:', err);
              }
            }
          }

          const keysVersion = raw.formattedMsg?.keysVersion ?? raw.keysVersion;
          const processed = await processIncomingMessage(
            messageText, Number(authUserId), peerAddress, resolvedSenderUsername, keysVersion, false, raw.formattedMsg?.created
          );

          // Clone the object and only swap the message string
          let decryptedPayload = { ...raw };
          if (raw.formattedMsg) {
            decryptedPayload.formattedMsg = {
              ...raw.formattedMsg,
              message: processed.decryptedMessage,
              isEncrypted: processed.isEncrypted,
              isKeyExchange: processed.isKeyExchange,
              ...(processed.senderUsername && { senderUsername: processed.senderUsername }),
              ...(processed.isUnverified && { isUnverified: true }),
              ...(processed.decryptStatus && { decryptStatus: processed.decryptStatus }),
            };
          } else if (raw.message) {
            decryptedPayload.message = processed.decryptedMessage;
          }

          // Stale signing key: fetch fresh key for sender then reload the conversation.
          // Covers the Android multi-device case where a new device generates new keys
          // and web still has the old signing key cached from the previous login.
          if (processed.needsKeyRefresh) {
            // Concurrent failures from the same sender share one fetch (refreshPublicKey).
            void refreshPublicKey(processed.needsKeyRefresh, Number(authUserId))
              .then(() => dispatch(setReloadMessages(true)));
          }

          // Missing or proven-stale group key (Path B self-heal): ask the owner; the arriving
          // distribution triggers setReloadMessages (below) to re-decrypt.
          const { username: selfUsername } = store.getState().auth;
          if (processed.needsGroupKeyRefresh && selfUsername && authUserId) {
            void requestGroupKey(processed.needsGroupKeyRefresh, selfUsername, Number(authUserId));
          }

          if (processed.isKeyExchange) {
            // keyArrivedFor scopes the per-address retry gates; the global counter always bumps.
            dispatch(keyExchangeReceived(processed.keyArrivedFor));

            // A key was stored (keyArrivedFor = a group's canonical address — covers
            // ECDH-wrapped distributions raw sniffing missed — or a peer's username). A group
            // distribution is always new; a 1:1 key only when the peer rotated — refreshing an
            // unchanged key must not defeat the cache.
            if (processed.keyArrivedFor) {
              const keyAddress = processed.keyArrivedFor;
              // Group addresses are {"gid",...} JSON; a plain username means a 1:1 key.
              const isGroupKey = (() => {
                try { return JSON.parse(keyAddress)?.gid !== undefined; } catch { return false; }
              })();
              if (isGroupKey || processed.keyChanged) redecryptConversation(keyAddress);
            }

            // If this is a group_key_request and we are the group owner, respond
            // with the encrypted symmetric key. This is the owner side of Path B —
            // members re-request the key when theirs is missing/stale.
            if (isGroupKeyRequestMessage(messageText)) {
              const parsed = parseGroupKeyRequestMessage(messageText);
              if (parsed) {
                const state = store.getState();
                const { userId: ownerId, username: ownerUsername } = state.auth;

                // The group_key_request arrives as a 1:1 message; raw.address is the sender
                const requesterUsername = raw.formattedMsg?.address || raw.address;

                if (ownerUsername && ownerId && requesterUsername) {
                  // Ownership + membership gate and the response live in answerGroupKeyRequest,
                  // shared with the history sweep (owner answering a request that arrived while
                  // it was offline). Fire-and-forget: don't block message delivery on it.
                  answerGroupKeyRequest({
                    requesterUsername,
                    groupId: parsed.group_id,
                    msgId: Number(raw.formattedMsg?.msgId),
                    ownerUsername,
                    ownerId: Number(ownerId),
                  }).catch((err) =>
                    console.error("[GroupKey] Failed to respond to key request:", err)
                  );
                }
              }
            }
          }
          dispatch(syncMessage(decryptedPayload));
        } else {
          dispatch(syncMessage(raw));
        }
      } catch (err) {
        console.error("Middleware: Error processing live message", err, raw);
        dispatch(syncMessage(raw)); // Always deliver the raw message rather than silently dropping it
      }
    };

    // Automated Key Exchange on Connection
    const setupEncryption = async () => {
      try {
        if (userId === undefined || userId === null || Number.isNaN(Number(userId))) {
          console.warn("E2E Encryption: Cannot initialize without a valid userId");
          return;
        }

        // Ensure the keypair exists on connect (idempotent — initializeEncryption returns
        // the cached keyId if already initialized). Publishing the public key to @keys is
        // handled by useEncryption.automateKeyExchange, so the middleware no longer
        // double-publishes it here.
        await initializeEncryption(userId);

        // Apply group keys distributed while web was closed — it otherwise only sees them live.
        // Runs on every (re)connect; a watermark keeps it to messages not yet scanned.
        const { username, routerE2eeEnabled } = store.getState().auth;
        if (username && routerE2eeEnabled) {
          for (const address of await catchUpGroupKeys(Number(userId), username)) {
            dispatch(keyExchangeReceived(address));
            redecryptConversation(address);
          }
        }
      } catch (err) {
        console.error("E2E Encryption: Automation failed", err);
      }
    };
    
    // Move encryption setup to 'connect' event to ensure Redux state
    // (routerId, username) is populated before background HTTP requests are made.
    // Also dispatch keyExchangeReceived() on connect so that ChatInputs' checkEncryption
    // effect re-runs with the socket live.  On first login the socket often finishes
    // connecting AFTER checkEncryption has already called requestPublicKey, causing the
    // @keys WebSocket response to be dropped (client not yet in the room).  The dispatch
    // resets lastKeyFetchAddressRef and triggers a fresh requestPublicKey while the
    // socket is confirmed connected, so the response is guaranteed to arrive.
    socket.on("connect", () => {
      console.log("WebSocket connected, setting up encryption automation...");
      dispatch(keyExchangeReceived());
      setupEncryption();
    });
    
    socket.off("receiveMessage").on("receiveMessage", (message) => {
      processAndDispatch(message);
    });

    socket.off("syncSendMessage").on("syncSendMessage", (message) => {
      processAndDispatch(message);
    });

    socket.on("disconnect", (reason, details) => {
      if (!socket?.active) {
        console.error(
          "websocket disconnected:",
          reason,
          new Date().toISOString()
        ); // the reason of the disconnection, for example "transport error"
        console.error(details); // the low-level reason of the disconnection, for example "xhr post error"
      }
    });

    socket.on("reconnect", async (n) => {
      console.log("Successfully reconnected after", n, "attempt(s)");
      const state = store.getState();
      const { conversations, currConversationIndex } = state.control;
      const { maxRmsgId, maxSmsgId } = state.chat;

      // update curr conversation messages + list of conversations
      if (currConversationIndex !== null) {
        const { address, addressId, addressType } = conversations[currConversationIndex];
        const newMessages = await fetchMessages(
          address,
          addressId,
          addressType,
          Number(state.auth.userId) || undefined,
          maxSmsgId,
          maxRmsgId
        );
        newMessages.messages.forEach(formattedMsg => {
          dispatch(syncMessage({ address, isGroup: addressType === GROUP_ADDRESS_TYPE, formattedMsg }));
        });
        dispatch(loadConversations());
      }

    });

    socket.on("reconnect_error", (err) => {
      console.error(err);
    });

    socket.on("reconnect_failed", () => {
      console.error("reconnect failed");
      dispatch(logout());
    });

    socket.on("connect_error", (err) => {
      console.error(err);
      if (err.message === "Unauthorized" || err.message.includes("jwt")) {
        dispatch(logout());
      }
    });

    socket.on("error", (err) => {
      console.error(err);
    });
  }

  if (disconnectSocket.match(action)) {
    socket?.disconnect();
    socket = null;
  }

  if (sendMessageSocket.match(action)) {
    const handleSend = async () => {
      const payload = { ...action.payload };
      const state = store.getState();
      const currentUserId = state.auth.userId;

      // Automated Encryption: Only for direct user-to-user messages
      if (payload.address_type === USERID_ADDRESS_TYPE && currentUserId && payload.address) {
        try {
          let peerKeys = await getPublicKeySetByUsername(payload.address);

          // On-demand fetch if key is missing
          if (!peerKeys) {
            await requestPublicKey(payload.address, Number(currentUserId));
            peerKeys = await getPublicKeySetByUsername(payload.address);
          }

          if (peerKeys) {
            const { getActiveKeySet } = await import("@/services/keyStorageService");
            const ownKeySet = await getActiveKeySet(Number(currentUserId));
            if (ownKeySet) {
              payload.message = await encryptMessage(
                payload.message,
                peerKeys.encryptPublicKeyJwk,
                ownKeySet.encryptPrivateKeyJwk,
                ownKeySet.signPrivateKeyJwk
              );
            }
          }
        } catch (error) {
          console.error("WebsocketMiddleware: Encryption failed, sending as plaintext", error);
        }
      }
      socket?.emit("sendMessage", payload);
    };
    handleSend();
  }

  return next(action);
};

export default websocketMiddleware;
